import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dashboardService from '../common/services/next-gen/DashboardService';

/**
 * Hook to fetch user dashboard
 */
export function useUserDashboard() {
    return useQuery({
        queryKey: ['userDashboard'],
        queryFn: dashboardService.getUserDashboard,
        staleTime: 60000 // 1 minute
    });
}

/**
 * Hook to fetch available widget definitions
 * Only fetches user-category widgets (not system widgets)
 */
export function useWidgetDefinitions() {
    return useQuery({
        queryKey: ['widgetDefinitions'],
        queryFn: () => dashboardService.getWidgetDefinitions('user'),
        staleTime: 600000 // 10 minutes
    });
}

/**
 * Hook to update dashboard layout (drag-and-drop)
 */
export function useUpdateDashboardLayout() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request) => dashboardService.updateDashboardLayout(request),
        onSuccess: (data) => {
            queryClient.setQueryData(['userDashboard'], data);
        }
    });
}

/**
 * Hook to add a widget
 */
export function useAddWidget() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (request) => dashboardService.addWidget(request),
        onSuccess: (data) => {
            queryClient.setQueryData(['userDashboard'], data);
        }
    });
}

/**
 * Hook to update a widget
 */
export function useUpdateWidget() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userDashboardWidgetId, request }) => 
            dashboardService.updateWidget(userDashboardWidgetId, request),
        onSuccess: (data, { userDashboardWidgetId }) => {
            queryClient.setQueryData(['userDashboard'], (old) => {
                if (!data) return old;
                if (!old?.widgets || !data?.widgets) return data;
                // The update endpoint only changes layout/title fields and never touches
                // filterConfig. Always restore the cached filterConfig for every widget
                // so a rename cannot wipe saved widget filters, regardless of what the
                // server echoes back (null, empty string, empty object, etc.).
                return {
                    ...data,
                    widgets: data.widgets.map(w => {
                        const cached = old.widgets.find(ow => ow.userDashboardWidgetId === w.userDashboardWidgetId);
                        return cached ? { ...w, filterConfig: cached.filterConfig } : w;
                    })
                };
            });
        }
    });
}

/**
 * Hook to remove a widget
 */
export function useRemoveWidget() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (userDashboardWidgetId) => dashboardService.removeWidget(userDashboardWidgetId),
        onSuccess: (data) => {
            queryClient.setQueryData(['userDashboard'], data);
        }
    });
}

/**
 * Hook to save widget filters
 */
export function useSaveWidgetFilters() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ userDashboardWidgetId, filterConfig }) => 
            dashboardService.saveWidgetFilters(userDashboardWidgetId, filterConfig),
        onSuccess: (data, { userDashboardWidgetId, filterConfig }) => {
            if (data) {
                // API returned the full updated dashboard � use it directly
                queryClient.setQueryData(['userDashboard'], data);
            } else {
                // API returned nothing � patch just this widget's filterConfig in place
                queryClient.setQueryData(['userDashboard'], (old) => {
                    if (!old?.widgets) return old;
                    return {
                        ...old,
                        widgets: old.widgets.map(w =>
                            w.userDashboardWidgetId === userDashboardWidgetId
                                ? { ...w, filterConfig: JSON.stringify(filterConfig) }
                                : w
                        )
                    };
                });
            }
        }
    });
}

/**
 * Hook to toggle collapsed state
 */
export function useToggleCollapsed() {
    return useMutation({
        mutationFn: ({ userDashboardWidgetId, isCollapsed }) => 
            dashboardService.toggleCollapsed(userDashboardWidgetId, isCollapsed)
    });
}
